import type { Candle } from "../../types";
import { CisdEngine } from "../../structure/CisdEngine";
import { DEFAULT_T50_CONFIG, T50Config, T50Engine, T50Event, evaluate, Excursion, Dir } from "./T50Engine";

// Research on the SAME candle/tick data: threshold sweep (frequency vs continuation), target-ladder hit rates,
// failure / hedge recovery, and CISD vs T50 vs CISD+T50 with one shared evaluator. Analytics never mutate the
// canonical config — they only report.
export interface StrategyStats {
  name: string;
  signals: number; bull: number; bear: number;
  frequency: number; // signals per 100 bars
  targetHit: Record<string, number>; // target → hit-rate % (completed windows)
  avgMfe: number; avgMae: number;
  continuation: number; // % windows reaching the canonical success target
  failRate: number; hedgeRate: number; hedgeRecovery: number;
  completed: number;
  score: number; // frequency × continuation (research balance metric)
}

function summarize(name: string, evs: { dir: Dir; failed?: boolean; hedge?: { recovered: boolean } | null; ex: Excursion }[], bars: number, cfg: T50Config): StrategyStats {
  const done = evs.filter((e) => e.ex.complete);
  const n = done.length || 1;
  const targetHit: Record<string, number> = {};
  for (const t of cfg.targets) targetHit[String(t)] = Math.round((done.filter((e) => e.ex.targets.find((x) => x.target === t)?.hit).length / n) * 1000) / 10;
  const failed = evs.filter((e) => e.failed).length, hedged = evs.filter((e) => e.hedge).length, recovered = evs.filter((e) => e.hedge?.recovered).length;
  const continuation = targetHit[String(cfg.successTarget)] ?? 0;
  const frequency = bars ? Math.round((evs.length / bars) * 10000) / 100 : 0;
  return { name, signals: evs.length, bull: evs.filter((e) => e.dir === "bull").length, bear: evs.filter((e) => e.dir === "bear").length, frequency, targetHit,
    avgMfe: Math.round((done.reduce((a, e) => a + e.ex.mfe, 0) / n) * 100) / 100, avgMae: Math.round((done.reduce((a, e) => a + e.ex.mae, 0) / n) * 100) / 100,
    continuation, failRate: evs.length ? Math.round((failed / evs.length) * 1000) / 10 : 0, hedgeRate: evs.length ? Math.round((hedged / evs.length) * 1000) / 10 : 0,
    hedgeRecovery: hedged ? Math.round((recovered / hedged) * 1000) / 10 : 0, completed: done.length, score: Math.round(frequency * continuation) / 100 };
}

const asRow = (e: T50Event) => ({ dir: e.dir, failed: e.failed, hedge: e.hedge, ex: e });

export const T50Research = {
  /** Same data, thresholds 50…70 (%) by default. */
  sweep(candles: Candle[], thresholds: number[] = Array.from({ length: 21 }, (_, i) => 50 + i), cfg: T50Config = DEFAULT_T50_CONFIG): (StrategyStats & { threshold: number })[] {
    return thresholds.map((pct) => {
      const r = T50Engine.analyze(candles, { ...cfg, threshold: pct / 100 }, { forming: false });
      return { threshold: pct, ...summarize(`T50 ${pct}%`, r.events.map(asRow), candles.length, cfg) };
    });
  },

  /** Tick-T50 two-dimensional research: micro-window × threshold on the same raw tick series. */
  matrix(candles: Candle[], windows: number[] = [3, 5, 7, 10, 15, 20], thresholds: number[] = [50, 55, 60, 65, 70], cfg: T50Config = DEFAULT_T50_CONFIG): (StrategyStats & { window: number; threshold: number })[] {
    const out: (StrategyStats & { window: number; threshold: number })[] = [];
    for (const w of windows) for (const pct of thresholds) {
      const r = T50Engine.analyzeTicks(candles, { ...cfg, microWindow: w, threshold: pct / 100 }, { forming: false });
      out.push({ window: w, threshold: pct, ...summarize(`T50 w${w} ${pct}%`, r.events.map(asRow), candles.length, cfg) });
    }
    return out;
  },

  /** CISD vs T50 vs CISD+T50 on identical candles with the identical 10-tick evaluator. */
  compare(candles: Candle[], cfg: T50Config = DEFAULT_T50_CONFIG, cisdWithin = 3): StrategyStats[] {
    const t50 = T50Engine.analyze(candles, cfg, { forming: false });
    const cisd = CisdEngine.analyze(candles);
    const cisdRows = cisd.events.filter((e) => candles[e.index + 1]).map((e) => {
      const entry = candles[e.index + 1];
      const ex = evaluate(candles, e.index + 1, entry.open, e.dir, cfg);
      const nb = candles[e.index + 1], failed = e.dir === "bull" ? nb.close < nb.open : nb.close > nb.open;
      return { dir: e.dir, failed, hedge: null, ex };
    });
    const cisdIdx = new Map<number, Dir>(cisd.events.map((e) => [e.index, e.dir]));
    const combo = t50.events.filter((ev) => { for (let k = 0; k <= cisdWithin; k++) if (cisdIdx.get(ev.index - k) === ev.dir) return true; return false; });
    return [
      summarize("CISD", cisdRows, candles.length, cfg),
      summarize(`T50 ${Math.round(cfg.threshold * 100)}%`, t50.events.map(asRow), candles.length, cfg),
      summarize(`CISD + T50`, combo.map(asRow), candles.length, cfg),
    ];
  },
};
